import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import ScopeBanner from '@/components/ScopeBanner';
import PendingTarBanner from '@/components/PendingTarBanner';
import { useNotifications } from '@/context/NotificationContext';
import { cn } from '@/lib/utils';
import {
  Coins,
  LayoutDashboard,
  Gauge,
  Truck,
  ClipboardList,
  Package,
  Bell,
  PackagePlus,
  ArrowRightLeft,
  Trash2,
  Scan,
  Receipt,
  Store,
  ShoppingBag,
  Wallet,
  Banknote,
  Table2,
  FileText,
  Settings,
  Warehouse,
  ShieldCheck,
  Users,
  Zap,
  Sparkles,
  Boxes,
  Building2,
  BookOpen,
  ChevronDown,
  ChevronRight,
  LogOut,
  Menu,
  X,
  ShoppingCart,
  RefreshCw
} from 'lucide-react';

// Navigation groups configuration - following new architecture
// RBAC 2026-09-15: Updated permissions, removed Go Live from sidebar
const NAV_GROUPS = [
  {
    id: 'painel',
    label: null,
    items: [
      { path: '/', label: 'Painel', icon: LayoutDashboard, perm: 'alerts.view' },
    ]
  },
  {
    id: 'compras',
    label: 'Compras',
    items: [
      { path: '/cockpit', label: 'Cockpit Compras', icon: Gauge, perm: 'po.view' },
      { path: '/supplier-orders', label: 'Fornecedores', icon: Truck, perm: 'suppliers.view' },
      { path: '/purchase-orders', label: 'Encomendas', icon: ClipboardList, perm: 'po.view' },
    ]
  },
  {
    id: 'stocks',
    label: 'Stocks',
    items: [
      { path: '/inventory', label: 'Inventário', icon: Package, perm: 'inventory.view' },
      { path: '/alerts', label: 'Alertas', icon: Bell, perm: 'alerts.view' },
      { path: '/van-replenishment', label: 'Reposição', icon: PackagePlus, perm: 'van.replenish' },
      { path: '/van-permanent-stock', label: 'Stock Permanente', icon: Truck, perm: 'van.replenish' },
      // RBAC: tar.view OR tar.create_from_own can see transfers
      { path: '/store-transfer', label: 'Transferências', icon: ArrowRightLeft, permAny: ['tar.view', 'tar.create_from_own'] },
      // RBAC: breakage.view OR breakage.create can see breakage page
      { path: '/breakage', label: 'Quebras e Consumos', icon: Trash2, permAny: ['breakage.view', 'breakage.create'] },
    ]
  },
  {
    id: 'servicos',
    label: 'Serviços Técnicos',
    items: [
      { path: '/fsm-services', label: 'Os Meus Serviços', icon: Scan, perm: 'consumption.view' },
      { path: '/invoicing', label: 'Faturação de Serviços', icon: Receipt, perm: 'invoicing.view' },
      { path: '/my-stock', label: 'Stock Carrinha', icon: Package, perm: 'van.manage_own' },
    ]
  },
  {
    id: 'lojas',
    label: 'Lojas',
    items: [
      { path: '/lojas', label: 'Dashboard', icon: Store, perm: 'analytics.view' },
      { path: '/lojas/vendas', label: 'Vendas', icon: ShoppingBag, permAny: ['analytics.view', 'retail.register_sales'] },
      { path: '/lojas/custos', label: 'Custos', icon: Coins, perm: 'analytics.view' },
      { path: '/lojas/fecho', label: 'Fecho de Caixa', icon: Wallet, permAny: ['analytics.view', 'retail.close_day'] },
      { path: '/lojas/depositos', label: 'Depósitos', icon: Banknote, perm: 'retail.deposits_manage' },
      { path: '/lojas/controle', label: 'Controlo Diário', icon: Table2, perm: 'retail.daily_control' },
    ]
  },
  {
    id: 'admin',
    label: 'Administração',
    items: [
      { path: '/warehouses', label: 'Armazéns', icon: Warehouse, perm: 'warehouses.view' },
      { path: '/lojas/auditoria', label: 'Auditoria Lojas', icon: FileText, perm: 'analytics.view' },
      { path: '/lojas/configuracao', label: 'Config. Lojas', icon: Settings, perm: 'analytics.import' },
      { path: '/users', label: 'Utilizadores', icon: Users, perm: 'users.view' },
      { path: '/admin/audit-log', label: 'Audit Log', icon: ShieldCheck, perm: 'audit.view' },
      { path: '/admin/data-quality', label: 'Data Quality', icon: ShieldCheck, perm: 'data_quality.view' },
      { path: '/admin/procurement', label: 'Procurement AI', icon: Sparkles, perm: 'procurement.governance' },
      { path: '/admin/unidades-compra', label: 'Unidades de Compra', icon: Boxes, perm: 'procurement.governance' },
      { path: '/fsm-admin', label: 'Integração FSM', icon: Zap, adminOnly: true },
      // RBAC 2026-09-15: Go Live removed from sidebar, route preserved for admin
      { path: '/company-settings', label: 'Perfil Empresa', icon: Building2, adminOnly: true },
      { path: '/sync-management', label: 'Sincronizações', icon: RefreshCw, adminOnly: true },
      { path: '/settings', label: 'Definições', icon: Settings, perm: 'settings.global' },
      { path: '/help', label: 'Manual', icon: BookOpen },
    ]
  },
];

// Tech bottom nav items - ordered by operational frequency
const TECH_NAV_ITEMS = [
  { path: '/fsm-services', label: 'Serviços', icon: Scan, perm: 'consumption.view' },
  { path: '/my-stock', label: 'Carrinha', icon: Package, perm: 'van.manage_own' },
  { path: '/breakage', label: 'Quebras', icon: Trash2, permAny: ['breakage.view', 'breakage.create'] },
  { path: '/store-transfer', label: 'TARs', icon: ArrowRightLeft, permAny: ['tar.view', 'tar.create_from_own'] },
];

// Check if user has permission for an item
// RBAC 2026-09-15: Added permAny support for OR logic
const hasPermissionForItem = (item, user, userPerms) => {
  if (item.adminOnly) return user?.role === 'admin';
  if (!item.perm && !item.permAny) return true;
  if (user?.role === 'admin') return true;
  // permAny: user needs ANY of the listed permissions
  if (item.permAny) {
    return item.permAny.some(p => userPerms.includes(p));
  }
  return userPerms.includes(item.perm);
};

// NavGroup component
function NavGroup({ group, isExpanded, onToggle, activePathname, userPerms, user, onNavigate, counters }) {
  const visibleItems = group.items.filter(item => hasPermissionForItem(item, user, userPerms));
  
  if (visibleItems.length === 0) return null;
  
  // Single item without group label (like Painel)
  if (!group.label) {
    return (
      <div className="mb-1">
        {visibleItems.map(item => (
          <NavItem
            key={item.path}
            item={item}
            isActive={activePathname === item.path}
            onClick={onNavigate}
            badge={counters?.[item.path]}
          />
        ))}
      </div>
    );
  }

  const hasActiveChild = visibleItems.some(item => 
    activePathname === item.path || 
    (item.path !== '/' && activePathname.startsWith(item.path))
  );

  const activeItemPath = visibleItems
    .filter(item => activePathname === item.path || (item.path !== '/' && activePathname.startsWith(`${item.path}/`)))
    .reduce((best, item) => (item.path.length > (best?.length || 0) ? item.path : best), null);

  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-md transition-colors",
          hasActiveChild 
            ? "text-slate-900 bg-slate-100" 
            : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
        )}
        data-testid={`nav-group-${group.id}`}
      >
        <span>{group.label}</span>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </button>
      
      {isExpanded && (
        <div className="mt-1 ml-2 space-y-0.5">
          {visibleItems.map(item => (
            <NavItem
              key={item.path}
              item={item}
              isActive={item.path === activeItemPath}
              onClick={onNavigate}
              badge={counters?.[item.path]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// NavItem component
function NavItem({ item, isActive, onClick, badge }) {
  const Icon = item.icon;
  
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
        isActive
          ? "bg-slate-800 text-white"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      )}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={isActive ? 2 : 1.5} />
      <span className="flex-1 truncate">{item.label}</span>
      {badge > 0 && (
        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

const Layout = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const { counters } = useNotifications();

  // Force smartphone-optimized layout for technicians
  const isTechMode = user?.role === 'technician';

  const userPerms = useMemo(() => 
    Array.isArray(user?.permissions) ? user.permissions : [],
    [user?.permissions]
  );

  // Check if user has cart access
  const hasCartAccess = useMemo(() => 
    userPerms.includes('cart.view') || user?.role === 'admin',
    [userPerms, user?.role]
  );
  
  // Cart count from notifications
  const cartCount = counters?.cart_items || 0;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Auto-expand group containing active route
  useEffect(() => {
    const activeGroup = NAV_GROUPS.find(group => 
      group.items.some(item => 
        location.pathname === item.path || 
        (item.path !== '/' && location.pathname.startsWith(item.path))
      )
    );
    if (activeGroup && activeGroup.label) {
      setExpandedGroups(prev => ({ ...prev, [activeGroup.id]: true }));
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // Navigation counters
  const navCounters = {
    '/store-transfer': counters?.inbox_tar_pending || 0,
  };

  // Filter tech nav items based on permissions
  const filteredTechNavItems = TECH_NAV_ITEMS.filter(item => {
    if (!item.perm) return true;
    if (user?.role === 'admin') return true;
    return userPerms.includes(item.perm);
  });

  const renderSidebarContent = (isMobile = false) => (
    <>
      {/* Logo - Compact */}
      <div className={cn("border-b border-slate-200", isMobile ? "p-4" : "p-4")}>
        <div className="flex items-center justify-center">
          <img 
            src="/chaviarte-logo.png" 
            alt="Chaviarte" 
            className={cn("object-contain", isMobile ? "h-10" : "h-10")}
          />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {NAV_GROUPS.map(group => (
          <NavGroup
            key={group.id}
            group={group}
            isExpanded={expandedGroups[group.id] !== false}
            onToggle={() => toggleGroup(group.id)}
            activePathname={location.pathname}
            userPerms={userPerms}
            user={user}
            onNavigate={isMobile ? () => setMobileMenuOpen(false) : undefined}
            counters={navCounters}
          />
        ))}
      </nav>

      {/* User section */}
      <div className="p-3 border-t border-slate-200 mt-auto">
        <div className="flex items-center gap-2 px-2 py-2 mb-2">
          <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-semibold text-slate-600">
              {user?.full_name?.charAt(0) || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            <div className="mt-1"><ScopeBanner /></div>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="w-full"
          data-testid="logout-button"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sair
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-white" data-role={user?.role || 'guest'} data-tech-mode={isTechMode ? 'on' : 'off'}>
      {/* Mobile Header - Compact 56px */}
      <header className={cn(
        "fixed top-0 left-0 right-0 h-14 bg-white/95 backdrop-blur-sm border-b border-slate-200 z-40 flex items-center justify-between px-4",
        isTechMode ? '' : 'lg:hidden'
      )}>
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="p-2 -ml-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
          data-testid="mobile-menu-button"
          aria-label="Abrir menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        
        {/* Logo - Compact, no slogan */}
        <div className="flex-1 flex justify-center">
          <img 
            src="/chaviarte-logo.png" 
            alt="Chaviarte" 
            className="h-6 object-contain"
          />
        </div>
        
        {/* Quick action - Cart for non-tech, empty space for tech */}
        {!isTechMode && hasCartAccess ? (
          <Link 
            to="/shopping-cart"
            className="p-2 -mr-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors relative"
            data-testid="header-cart-link-mobile"
            aria-label={`Carrinho${cartCount > 0 ? ` (${cartCount} artigos)` : ''}`}
          >
            <ShoppingCart className="h-5 w-5" />
            {cartCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[#C41E3A] text-white text-[10px] font-bold px-1">
                {cartCount > 99 ? '99+' : cartCount}
              </span>
            )}
          </Link>
        ) : (
          <div className="w-9" /> /* Spacer for balance */
        )}
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className={cn("fixed inset-0 bg-black/50 z-40", isTechMode ? '' : 'lg:hidden')}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div 
        className={cn(
          "fixed left-0 top-0 h-full w-72 bg-white z-50 transform transition-transform duration-300 ease-in-out flex flex-col shadow-xl",
          isTechMode ? '' : 'lg:hidden',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <button
          onClick={() => setMobileMenuOpen(false)}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 z-10"
          data-testid="close-mobile-menu"
        >
          <X className="h-5 w-5" />
        </button>
        {renderSidebarContent(true)}
      </div>

      {/* Desktop Sidebar — hidden entirely in tech mode */}
      {!isTechMode && (
        <div className="hidden lg:flex lg:fixed left-0 top-0 h-full w-60 bg-white border-r border-slate-200 z-30 flex-col">
          {renderSidebarContent(false)}
        </div>
      )}

      {/* Desktop Top Bar — Cart access + Global actions — hidden in tech mode */}
      {!isTechMode && (
        <div className="hidden lg:flex fixed top-0 left-60 right-0 h-12 bg-white border-b border-slate-200 z-20 items-center justify-end px-4 gap-3">
          {/* Cart button with counter */}
          {hasCartAccess && (
            <Link
              to="/shopping-cart"
              className={cn(
                "inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                "text-slate-600 hover:text-slate-900 hover:bg-slate-100",
                location.pathname === '/shopping-cart' && "bg-slate-100 text-slate-900"
              )}
              data-testid="header-cart-link-desktop"
              title="Carrinho de Compras"
            >
              <ShoppingCart className="h-4 w-4" />
              <span>Carrinho</span>
              {cartCount > 0 && (
                <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-[#C41E3A] text-white text-xs font-bold px-1.5">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className={cn(
        "flex-1 min-w-0",
        isTechMode ? 'pt-14 pb-16' : 'lg:pl-60 pt-14 lg:pt-12'
      )}>
        <PendingTarBanner />
        <Outlet />
      </div>

      {/* Bottom Nav Bar — tech mode only — Native app style */}
      {isTechMode && (
        <nav
          className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-slate-200 flex items-stretch safe-area-bottom"
          data-testid="tech-bottom-nav"
        >
          {filteredTechNavItems.map(({ path, label, icon: Icon }) => {
            const isActive = location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
            const badge = navCounters[path] || 0;
            return (
              <Link
                key={path}
                to={path}
                data-testid={`tech-nav-${label.toLowerCase()}`}
                className={cn(
                  "flex-1 flex flex-col items-center justify-center py-2 min-h-[56px] transition-colors",
                  isActive 
                    ? 'text-[#C41E3A]' 
                    : 'text-slate-500 active:bg-slate-100'
                )}
              >
                <span className="relative">
                  <Icon className="h-5 w-5" strokeWidth={isActive ? 2 : 1.5} />
                  {badge > 0 && (
                    <span className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#C41E3A] text-white text-[10px] font-semibold flex items-center justify-center">
                      {badge}
                    </span>
                  )}
                </span>
                <span className={cn(
                  "text-[10px] mt-0.5",
                  isActive ? 'font-medium' : 'font-normal'
                )}>
                  {label}
                </span>
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
};

export default Layout;
